import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';

export const HomeStudyBlocks: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Módulos de Estudo
        </h2>
        <p className="text-xs text-zinc-600 dark:text-zinc-400">
          Selecione a atividade do seu bloco de treino atual.
        </p>
      </div>

      {/* Asymmetric grid: Hero card + 4 specialized cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Featured Hero Card: Triagem de Questões (Spans 2 columns) */}
        <div
          onClick={() => navigate('/triagem')}
          className="md:col-span-2 cursor-pointer rounded-xl border-2 border-study-teal-600/40 bg-paper-100 p-6 shadow-sm transition hover:border-study-teal-600 hover:shadow-md dark:border-study-teal-500/30 dark:bg-study-panel dark:hover:border-study-teal-400 group flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-study-teal-100 px-2.5 py-0.5 text-xs font-semibold text-study-teal-800 dark:bg-study-teal-950/60 dark:text-study-teal-300">
                <span className="h-1.5 w-1.5 rounded-full bg-study-teal-600 dark:bg-study-teal-400" />
                Treino deliberado · Cronometrado
              </span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                Meta: 3 min / questão
              </span>
            </div>

            <h3 className="mt-3 font-serif text-xl font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-study-teal-700 dark:group-hover:text-study-teal-300 transition">
              Triagem de Questões
            </h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-xl">
              Isole dados fornecidos, incógnitas e o modelo conceitual antes de calcular. Treine a identificação imediata de pegadinhas e da fórmula-ponte necessária.
            </p>

            {/* Visual Process Flow Strip */}
            <div className="mt-4 flex items-center gap-2 text-xs font-mono text-zinc-600 dark:text-zinc-400 overflow-x-auto pb-1">
              <span className="rounded bg-paper-200 px-2 py-1 dark:bg-study-card text-zinc-800 dark:text-zinc-200">
                1. Enunciado
              </span>
              <span className="text-zinc-400">→</span>
              <span className="rounded bg-paper-200 px-2 py-1 dark:bg-study-card text-zinc-800 dark:text-zinc-200">
                2. Dados & Incógnita
              </span>
              <span className="text-zinc-400">→</span>
              <span className="rounded bg-study-amber-100 px-2 py-1 text-study-amber-900 dark:bg-study-amber-950/50 dark:text-study-amber-300 font-semibold">
                3. Fórmula-Ponte
              </span>
              <span className="text-zinc-400">→</span>
              <span className="rounded bg-study-teal-100 px-2 py-1 text-study-teal-900 dark:bg-study-teal-950/50 dark:text-study-teal-300 font-semibold">
                4. Gabarito
              </span>
            </div>
          </div>

          <div className="mt-5 pt-3 border-t border-paper-200 dark:border-study-line flex items-center justify-between text-xs">
            <span className="text-zinc-500 dark:text-zinc-400">
              Banco oficial IFF · 250 questões com matriz taxonômica
            </span>
            <span className="inline-flex items-center gap-1 font-semibold text-study-teal-700 group-hover:translate-x-0.5 dark:text-study-teal-400 transition-transform">
              Entrar na sessão
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>

        {/* Card 2: Grafo de Fórmulas */}
        <div
          onClick={() => navigate('/grafo')}
          className="cursor-pointer rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm transition hover:border-study-teal-500 hover:shadow-md dark:border-study-line dark:bg-study-panel dark:hover:border-study-teal-400 group flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-study-teal-700 dark:text-study-teal-400">
                Solver BFS de Física & Matemática
              </span>
            </div>

            <h3 className="mt-2.5 font-serif text-base font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-study-teal-700 dark:group-hover:text-study-teal-300 transition">
              Grafo de Fórmulas
            </h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Encontre o menor caminho de dedução conectando as variáveis do problema à incógnita.
            </p>

            {/* Visual Formula Deduction Snippet */}
            <div className="mt-3.5 rounded-lg border border-paper-300 bg-paper-50 p-2.5 dark:border-study-line dark:bg-study-card">
              <div className="flex items-center justify-center gap-2 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                <span className="text-study-amber-600 dark:text-study-amber-400">[v₀, a, t]</span>
                <span className="text-zinc-400">➔</span>
                <span className="font-semibold text-study-teal-700 dark:text-study-teal-300">v = v₀ + at</span>
                <span className="text-zinc-400">➔</span>
                <span className="text-study-teal-600 dark:text-study-teal-400">[v]</span>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-2.5 border-t border-paper-200 dark:border-study-line flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
            <span>Rede de dependências</span>
            <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>

        {/* Card 3: Caderno de Erros */}
        <div
          onClick={() => navigate('/dashboard')}
          className="cursor-pointer rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm transition hover:border-zinc-400 hover:shadow-md dark:border-study-line dark:bg-study-panel dark:hover:border-zinc-400 group flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-rose-700 dark:text-rose-400">
                Diagnóstico de falhas
              </span>
            </div>

            <h3 className="mt-2.5 font-serif text-base font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-rose-700 dark:group-hover:text-rose-400 transition">
              Caderno de Erros
            </h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Auditoria de causa-raiz para eliminar reincidência de erros teóricos, esquecimento ou cálculo.
            </p>

            {/* Diagnostic Categories */}
            <div className="mt-3.5 flex flex-wrap gap-1.5 text-[10px]">
              <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-900/50">
                Teoria
              </span>
              <span className="rounded bg-study-amber-50 px-2 py-0.5 text-study-amber-800 dark:bg-study-amber-950/40 dark:text-study-amber-300 border border-study-amber-200 dark:border-study-amber-900/50">
                Fórmula
              </span>
              <span className="rounded bg-blue-50 px-2 py-0.5 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50">
                Cálculo
              </span>
              <span className="rounded bg-study-teal-50 px-2 py-0.5 text-study-teal-800 dark:bg-study-teal-950/40 dark:text-study-teal-300 border border-study-teal-200 dark:border-study-teal-900/50">
                Leitura
              </span>
            </div>
          </div>

          <div className="mt-4 pt-2.5 border-t border-paper-200 dark:border-study-line flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
            <span>Taxonomia de falhas</span>
            <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>

        {/* Card 4: Cronograma de Estudos */}
        <div
          onClick={() => navigate('/calendario')}
          className="cursor-pointer rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm transition hover:border-zinc-400 hover:shadow-md dark:border-study-line dark:bg-study-panel dark:hover:border-zinc-400 group flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-study-amber-700 dark:text-study-amber-400">
                Rotina diária
              </span>
            </div>

            <h3 className="mt-2.5 font-serif text-base font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-study-amber-700 dark:group-hover:text-study-amber-400 transition">
              Cronograma de Estudos
            </h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Distribuição dos blocos em três turnos com síntese estrita de até 30 palavras por matéria.
            </p>

            {/* Shift Timeline Indicator */}
            <div className="mt-3.5 grid grid-cols-3 gap-1.5 text-center text-[11px]">
              <div className="rounded border border-paper-300 bg-paper-50 py-1 font-medium text-zinc-700 dark:border-study-line dark:bg-study-card dark:text-zinc-300">
                Manhã
              </div>
              <div className="rounded border border-paper-300 bg-paper-50 py-1 font-medium text-zinc-700 dark:border-study-line dark:bg-study-card dark:text-zinc-300">
                Tarde
              </div>
              <div className="rounded border border-paper-300 bg-paper-50 py-1 font-medium text-zinc-700 dark:border-study-line dark:bg-study-card dark:text-zinc-300">
                Noite
              </div>
            </div>
          </div>

          <div className="mt-4 pt-2.5 border-t border-paper-200 dark:border-study-line flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
            <span>Planejamento por turno</span>
            <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>

        {/* Card 5: Upload de Provas */}
        <div
          onClick={() => navigate('/upload')}
          className="cursor-pointer rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm transition hover:border-zinc-400 hover:shadow-md dark:border-study-line dark:bg-study-panel dark:hover:border-zinc-400 group flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Acervo de cadernos
              </span>
            </div>

            <h3 className="mt-2.5 font-serif text-base font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 transition">
              Upload e Provas Oficiais
            </h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Indexação de novos cadernos em PDF mapeados contra os descritores do edital IFF.
            </p>

            {/* Edition Tags */}
            <div className="mt-3.5 flex flex-wrap gap-1.5 text-[10px] font-mono text-zinc-600 dark:text-zinc-400">
              <span className="rounded bg-paper-200 px-1.5 py-0.5 dark:bg-study-card">2024.1</span>
              <span className="rounded bg-paper-200 px-1.5 py-0.5 dark:bg-study-card">2024.2</span>
              <span className="rounded bg-paper-200 px-1.5 py-0.5 dark:bg-study-card">2025.1</span>
              <span className="rounded bg-paper-200 px-1.5 py-0.5 dark:bg-study-card">2025.2</span>
              <span className="rounded bg-paper-200 px-1.5 py-0.5 dark:bg-study-card">2026.1</span>
            </div>
          </div>

          <div className="mt-4 pt-2.5 border-t border-paper-200 dark:border-study-line flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
            <span>Anexo VI Matriz</span>
            <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      </div>
    </div>
  );
};
